/**
 * Which audited actions must carry a REASON — PAGCOR item 41.0.
 *
 * "Record the reason for a data alteration" (RF: p. 49 - V.4.o). The trail
 * already holds who, when and before/after; the reason was carried inside the
 * `after` payload by handler convention, and the convention was barely followed —
 * at the time this file was written, 12 of 6,326 rows had one.
 *
 * ── THE LINE THIS FILE DRAWS, AND WHY IT IS NOT "EVERY ACTION" ─────────────
 *
 * Item 41 asks for the reason a record was ALTERED. It does not ask why somebody
 * signed in, why a deposit settled, or why a nightly job stamped a first deposit —
 * and demanding one there would produce a column full of sentences written to
 * satisfy a validator. A reason nobody chose is worse than no reason: it looks
 * like evidence and is filler.
 *
 * ── ONE RULE ──────────────────────────────────────────────────────────────
 *
 * A REASON IS REQUIRED WHEN A PERSON ALTERS A RECORD THAT IS NOT THEIR OWN.
 *
 * That single sentence decides every entry below, and it replaced three clauses
 * and a list of special pleadings on the first draft of this file. Each half of it
 * is doing work:
 *
 *   A PERSON. Not a job, not a webhook, not a provider's callback.
 *   `RISK_ALERT_RAISED` is a rule firing and `provider.sa.balance_adjustment` is
 *   SA moving a balance; neither has anybody to ask. `RISK_ALERT_CLOSED` is
 *   somebody deciding, and does.
 *
 *   ALTERS. Creating a game, drafting a promotion or inviting an admin brings a
 *   record into existence — there is no prior state to justify the change against.
 *   Changing that game's status later is an alteration and needs one.
 *
 *   NOT THEIR OWN. This is the half that does the most work and the half the
 *   first draft got wrong by arguing case by case. A player self-excluding, a
 *   player turning off their own second factor, a partner updating their own
 *   payout account — each is a person altering a record, and in every case the
 *   person IS the subject. Demanding they justify a decision about themselves is
 *   intrusive, it is friction on exactly the self-service controls that should be
 *   easiest to reach, and self-exclusion must be the easiest control on this
 *   platform to use. What protects those records is the trail itself and a
 *   notification to the holder, not a reason the holder wrote about their own
 *   account.
 *
 * ── AND TWO THINGS THE RULE EXCLUDES THAT ARE WORTH NAMING ────────────────
 *
 * MONEY ARRIVING AND LEAVING ON ITS OWN RAILS — `DEPOSIT_SETTLED`,
 * `WITHDRAWAL_RELEASED`, `BONUS_CONVERTED`. The reason is the event. What DOES
 * need one is a refusal or a reversal, because those are judgements.
 *
 * EXPORTS — `report.run`, `AUDIT_LOG_EXPORTED`, `PLAYER_ROSTER_EXPORTED`. Reading
 * is not altering. A purpose-of-access field is a real and separate idea, and
 * `player.identity_document_access.purpose` is where this platform does it for the
 * one surface where it matters most; folding it in here would conflate two rules
 * and weaken both.
 *
 * ── ONE LIST, THREE READERS ───────────────────────────────────────────────
 *
 * This constant, the CHECK in `packages/db/sql/996_audit_reason.sql`, and the
 * census test in `services/api/src/__tests__/audit-reason-census.integration.test.ts`
 * must agree. They cannot be collapsed — one is TypeScript, one is SQL, one reads
 * the source — so the test asserts all three say the same thing, the way the
 * minimum-age floor is held in three places and checked for agreement.
 */

/**
 * The actions whose rows must carry a reason.
 *
 * Alphabetical within each group so a new one is easy to place, and grouped
 * because the groups are the argument: every entry is a person overriding,
 * refusing, or changing a rule that was already in force.
 */
export const AUDIT_ACTIONS_NEEDING_A_REASON = [
  /* ── Verdicts on somebody's money ──────────────────────────────────────── */
  'ADJUSTMENT_APPROVED',
  'ADJUSTMENT_REJECTED',
  'AFFILIATE_PAYOUT_APPROVED',
  'AFFILIATE_PAYOUT_REJECTED',
  'AGENT_PAYOUT_APPROVED',
  'AGENT_PAYOUT_REJECTED',
  'LEDGER_REVERSAL_APPROVED',
  'LEDGER_REVERSAL_REJECTED',
  'POINTS_ADJUSTMENT_APPROVED',
  'POINTS_ADJUSTMENT_REJECTED',
  'REDEMPTION_APPROVED',
  'REDEMPTION_REJECTED',
  'WITHDRAWAL_REJECTED',
  'WITHDRAWAL_UNPARKED',

  /* ── Verdicts on who somebody is ───────────────────────────────────────── */
  'IDENTITY_APPROVED',
  'IDENTITY_REFUSED',
  'KYC_APPROVED',
  'KYC_REJECTED',
  'PAYOUT_ACCOUNT_REVIEWED',

  /* ── Closing or opening an account's doors ─────────────────────────────── */
  'ADMIN_REINSTATED',
  'ADMIN_SESSIONS_ENDED',
  'ADMIN_SUSPENDED',
  'AGENT_REINSTATED',
  'AGENT_SUSPENDED',
  /* Support allowing a refused player one more identity try: the refusal closed the account's way
     to being verified, and this opens it again for one check. */
  'IDENTITY_RETRY_ALLOWED',
  'PARTNER_PORTAL_ACCESS_REINSTATED',
  'PARTNER_PORTAL_ACCESS_SUSPENDED',
  'PLAYER_FLAGGED',
  'PLAYER_REINSTATED',
  'PLAYER_RESTRICTED',
  'PLAYER_RESTRICTION_LIFTED',
  'PLAYER_SESSIONS_ENDED',
  'PLAYER_SUSPENDED',

  /* ── Forcing a credential, which locks somebody out until they act ───────
     An OPERATOR doing this to somebody else. The holder doing it to themselves —
     `PLAYER_TWO_FACTOR_DISENROLLED`, `PLAYER_RECOVERY_CODES_REISSUED`,
     `PLAYER_PASSWORD_CHANGED` — is excluded by the rule above. */
  'ADMIN_TWO_FACTOR_RESET',
  'PARTNER_PORTAL_CREDENTIAL_RESET',
  'PLAYER_PASSWORD_RESET_FORCED',

  /* ── Changing what the platform will do, for everyone ──────────────────── */
  'GAME_BRANDS_CHANGED',
  'GAME_BRAND_AVAILABILITY_CHANGED',
  'GAME_CONFIG_CHANGED',
  'GAME_STATUS_CHANGED',
  /* A rail offered to one brand's players or taken away, and a rail's limits for each deposit
     or withdrawal: both reach players at their next request. */
  'PAYMENT_METHOD_BRAND_CHANGED',
  'PAYMENT_METHOD_LIMITS_CHANGED',
  'PAYMENT_RAIL_FEE_BEARER_CHANGED',
  'PAYMENT_RAIL_STATUS_CHANGED',
  'PAYOUT_BREAKER_RESET',
  'POINTS_EARNING_RULE_CHANGED',
  'PROVIDER_CONFIG_CHANGED',
  'PROVIDER_STATUS_CHANGED',
  'RISK_RULE_STATE_CHANGED',
  'RISK_RULE_THRESHOLD_CHANGED',

  /* ── Changing what somebody is owed or allowed ───────────────────────────
     An operator changing a partner's terms. A partner changing their OWN payout
     account is excluded by the rule: they are the subject, and what guards that
     record is the trail plus a notice to the holder. */
  'ADMIN_BRAND_SCOPE_CHANGED',
  'ADMIN_ROLE_CHANGED',
  'AFFILIATE_PLAN_CHANGED',
  'AGENT_PLAN_ASSIGNED',
  'AGENT_SPLIT_CHANGE_APPLIED',
  'AGENT_SPLIT_CHANGE_REFUSED',
  'PLAYER_TIER_CHANGED',

  /* ── Stopping something already in motion ──────────────────────────────── */
  'ANNOUNCEMENT_SEND_CANCELLED',
  'BONUS_FORFEITED',
  'MAINTENANCE_WINDOW_CANCELLED',
  'PROMOTION_CANCELLED',
  'PROMOTION_PAUSED',
  'PROMOTION_RESUMED',
  'PROMOTION_SUPERSEDED',
  'RISK_ALERT_CLOSED',
  'RISK_CASE_RESOLVED',
  /* A StarPay notification that named no payout, taken off the count of what waits for a
     person. The notification itself is StarPay's and is left as it was filed. */
  'WEBHOOK_INBOX_SEEN',

  /* ── Reaching into a provider's ledger ───────────────────────────────────
     An operator closing a reconciliation difference. `provider.sa.balance_adjustment`
     is NOT here: that is SA itself moving a balance, and there is nobody to ask. */
  'provider.sa.reconciliation_resolved',
] as const;

export type AuditActionNeedingAReason = (typeof AUDIT_ACTIONS_NEEDING_A_REASON)[number];

export function auditActionNeedsAReason(action: string): boolean {
  return (AUDIT_ACTIONS_NEEDING_A_REASON as readonly string[]).includes(action);
}

/**
 * The shortest reason worth storing.
 *
 * Ten characters. Not a number chosen for neatness: it is long enough to refuse
 * "ok", "n/a", "per policy" and "-", which is what a required field collects when
 * the person filling it has nothing to say. A reason that explains nothing is the
 * failure mode this item exists to prevent, and it is not improved by being
 * mandatory.
 *
 * The database enforces the same floor, so a writer that bypasses the handler
 * cannot store "ok" either.
 */
export const AUDIT_REASON_MIN_LENGTH = 10;
